/*
 * Jane Lushka v1.2: product page buy box (phase 3; pdp:4, pdp:6, pdp:8, pdp:9).
 *
 * Defines window.JLPdp (contract "JS API and DOM events"):
 *   getState(sectionId?)                  -> { section, variantId, size, available, needsSize, soldOutAll, price, low, ready }
 *   selectSize(value, { source })         -> checks the size radio and fires one bubbling change (variants.js, GA4 #12)
 *   requireSize(source)                   -> size error, focus to the first size radio for 'inline', jl:pdp-size-required
 *   openNotify(variantId, { focus, source }) -> notify panel for that variant, jl:bis-open
 *   _dispatchState(date, cutoff, closed) -> 'before-cutoff' | 'ready' | 'next' (Europe/Amsterdam, pure)
 * Events on document: jl:pdp-ready, jl:pdp-state, jl:pdp-size-required, jl:bis-open, jl:bis-signup,
 *   jl:pdp-add-error { section, message, source, variant_id } (a failed /cart/add; message '' clears it).
 *
 * Load order (L2): deferred from sections/main-product.liquid, so it runs BEFORE variants.js and product-form.js
 * (html-bottom-js). It patches <variant-radios> only after customElements.whenDefined('variant-radios'), and only
 * for elements with data-jl-pdp-variants (the quick shop keeps the original methods).
 * Render is idempotent and reads the checked radios plus the data map; it never fetches (L6). jl:pdp-state fires
 * only when variantId, needsSize or available changed (or on init). Announcements come only from the radio change
 * handler. All text comes from the data map (Liquid); prices are Liquid | money strings.
 * Adds always go through product-form.js: this file never posts /cart/add and never opens the bag. Its
 * capture-phase submit guard (L8) only stops the add form inside [data-jl-pdp] while no size is chosen.
 * A small fetch wrapper (installed once) reads a clone of the /cart/add response of a PDP add, so a failed add
 * (422, network) shows a message instead of only console.error in product-form.js.
 */
(function () {
  'use strict';

  if (window.JLPdp && window.JLPdp.__v12) return;

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  /* the formats snippets/jl-trust-facts.liquid accepts (it hands over HH:MM): H:MM, HH:MM, HH.MM, HHhMM */
  var CUTOFF_RE = /^([01]?\d|2[0-3])[:.hH]([0-5]\d)$/;
  var BIS_TIMEOUT = 10000;
  var cache = {};
  var amsFmt = null;
  var addPending = null;

  /* ---------------- helpers ---------------- */

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function fill(str, map) {
    var out = String(str == null ? '' : str);
    Object.keys(map || {}).forEach(function (key) {
      out = out.split('[' + key + ']').join(map[key] == null ? '' : String(map[key]));
    });
    return out;
  }

  function emit(name, detail) {
    var ev;
    try {
      ev = new CustomEvent(name, { detail: detail, bubbles: false });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, false, false, detail);
    }
    document.dispatchEvent(ev);
  }

  function setHidden(el, hidden) {
    if (el && el.hidden !== !!hidden) el.hidden = !!hidden;
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function rootFor(sectionId) {
    if (sectionId) {
      var all = qsa('[data-jl-pdp]');
      for (var i = 0; i < all.length; i++) {
        if (all[i].getAttribute('data-section-id') === String(sectionId)) return all[i];
      }
      return null;
    }
    return qs('[data-jl-pdp]');
  }

  /* per-section context; re-read when the section (or its data map) was replaced */
  function ctx(root) {
    if (!root) return null;
    var id = root.getAttribute('data-section-id') || '';
    var dataEl = qs('[data-jl-pdp-data]', root);
    var c = cache[id];
    if (!c || c.root !== root || c.dataEl !== dataEl) {
      var data = null;
      try {
        data = dataEl ? JSON.parse(dataEl.textContent) : null;
      } catch (e) {
        data = null;
      }
      data = data || {};
      data.variants = data.variants || {};
      data.strings = data.strings || {};
      c = cache[id] = {
        id: id, root: root, dataEl: dataEl, data: data,
        lastKey: null, notifyFor: null, bisOpenFor: null, notifyDone: {}, pending: false,
        pendingSource: null, mode: null, addErrFor: null
      };
    }
    return c;
  }

  function sizeFieldset(c) { return qs('[data-jl-size-fieldset]', c.root); }

  /* ---------------- state ---------------- */

  function computeState(c) {
    var data = c.data;
    var radios = qs('[data-jl-pdp-variants]', c.root);
    var fieldsets = radios ? qsa('fieldset', radios) : [];
    var selected = fieldsets.map(function (fs) {
      var r = qs('input[type="radio"]:checked', fs);
      return r ? r.value : null;
    });
    var complete = selected.every(function (v) { return v !== null; });
    var sizeFs = sizeFieldset(c);
    var sizeRadio = sizeFs ? qs('input[type="radio"]:checked', sizeFs) : null;
    var variantId = null;

    if (!fieldsets.length) {
      variantId = data.preselected || Object.keys(data.variants)[0] || null;
    } else if (complete) {
      var ids = Object.keys(data.variants);
      for (var i = 0; i < ids.length; i++) {
        var opts = data.variants[ids[i]].options || [];
        var match = true;
        for (var j = 0; j < selected.length; j++) {
          if (String(opts[j]) !== String(selected[j])) { match = false; break; }
        }
        if (match) { variantId = ids[i]; break; }
      }
    }

    var v = variantId ? data.variants[String(variantId)] : null;
    return {
      section: c.id,
      variantId: variantId ? Number(variantId) : null,
      size: v && v.size ? v.size : (sizeRadio ? sizeRadio.value : null),
      available: !!(v && v.available),
      needsSize: !!fieldsets.length && !complete,
      soldOutAll: !!data.soldOutAll,
      price: v ? v.price : null,
      low: v && v.available ? (v.low || '') : '',
      ready: !!(v && v.ready)
    };
  }

  /* ---------------- delivery clock (pure) ---------------- */

  /* PF1: one formatter per page (the first one loads the time zone data); warmed when idle after jl:pdp-ready */
  function amsFormatter() {
    if (!amsFmt) {
      amsFmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Amsterdam', weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      });
    }
    return amsFmt;
  }

  /* cut-off: blank -> 'ready' on a working day (L9); set but unreadable (data-cutoff "invalid") -> no same-day
     claim, 'next' */
  function dispatchState(date, cutoff, closedDays) {
    var d = date instanceof Date ? date : new Date(date == null ? Date.now() : date);
    var parts = {};
    try {
      amsFormatter().formatToParts(d).forEach(function (p) { parts[p.type] = p.value; });
    } catch (e) {
      return 'next';
    }
    var ymd = parts.year + '-' + parts.month + '-' + parts.day;
    var list = Array.isArray(closedDays) ? closedDays : String(closedDays || '').split(/[\s,;]+/);
    var closed = list.map(function (s) { return String(s).trim(); }).filter(Boolean);
    if (parts.weekday === 'Sat' || parts.weekday === 'Sun' || closed.indexOf(ymd) !== -1) return 'next';
    var cutText = String(cutoff == null ? '' : cutoff).trim();
    var m = CUTOFF_RE.exec(cutText);
    if (!m) return cutText ? 'next' : 'ready';
    var hour = Number(parts.hour) % 24;
    var now = hour * 60 + Number(parts.minute);
    var cut = Number(m[1]) * 60 + Number(m[2]);
    return now >= cut ? 'next' : 'before-cutoff';
  }

  function renderDelivery(c, st) {
    var box = qs('[data-jl-delivery]', c.root);
    if (!box) return;
    var mode = null;
    if (box.getAttribute('data-ready-on') === 'true' && st.variantId && st.available && st.ready) {
      mode = dispatchState(new Date(), box.getAttribute('data-cutoff'), box.getAttribute('data-closed'));
    }
    setHidden(qs('[data-jl-delivery-ready]', box), !(mode === 'ready' || mode === 'before-cutoff'));
    setHidden(qs('[data-jl-delivery-cutoff]', box), mode !== 'before-cutoff');
    setHidden(qs('[data-jl-delivery-instock]', box), mode !== 'next');
    setHidden(qs('[data-jl-delivery-next]', box), mode !== 'next');
  }

  /* ---------------- back in stock (Amp) ---------------- */

  function bisMode() {
    var B = window.BIS;
    if (B && typeof B.create === 'function') return 'inline';
    if (B && B.popup && typeof B.popup.show === 'function') return 'popup';
    return 'app';
  }

  function applyMode(c) {
    var panel = qs('[data-jl-notify]', c.root);
    if (!panel) return;
    var mode = bisMode();
    c.mode = mode;
    var done = c.notifyFor && c.notifyDone[c.notifyFor];
    setHidden(qs('[data-jl-notify-form]', panel), mode !== 'inline' || !!done);
    setHidden(qs('.jl-notify__privacy', panel), mode !== 'inline' || !!done);
    setHidden(qs('[data-jl-notify-popup]', panel), mode !== 'popup');
    c.root.classList.toggle('jl-bis-app-visible', mode === 'app' && !!c.notifyFor);
  }

  function setNotifyMsg(c, text, kind) {
    var msg = qs('[data-jl-notify-msg]', c.root);
    if (!msg) return;
    setText(msg, text || '');
    msg.classList.toggle('jl-notify__msg--error', kind === 'error');
    msg.classList.toggle('jl-notify__msg--ok', kind === 'ok');
  }

  /* show the panel for a variant; returns true when it was closed or showed another variant before */
  function showNotify(c, variantId) {
    var panel = qs('[data-jl-notify]', c.root);
    if (!panel) return false;
    var vid = Number(variantId);
    var v = c.data.variants[String(vid)] || {};
    var S = c.data.strings;
    var changed = c.notifyFor !== vid || panel.hidden;
    if (changed) {
      c.notifyFor = vid;
      setText(qs('[data-jl-notify-title]', panel), fill(S.notifyTitle, { size: v.size || '' }));
      var email = qs('[data-jl-notify-email]', panel);
      if (email) email.removeAttribute('aria-invalid');
      var submit = qs('[data-jl-notify-submit]', panel);
      if (submit && !c.pending) submit.disabled = false;
      if (c.notifyDone[vid]) setNotifyMsg(c, fill(S.notifySuccess, { size: v.size || '' }), 'ok');
      else setNotifyMsg(c, '', null);
    }
    setHidden(panel, false);
    applyMode(c);
    return changed;
  }

  function hideNotify(c) {
    var panel = qs('[data-jl-notify]', c.root);
    if (panel) setHidden(panel, true);
    c.notifyFor = null;
    c.bisOpenFor = null;
    c.root.classList.remove('jl-bis-app-visible');
  }

  function bisOpen(c, variantId, source) {
    var vid = Number(variantId);
    if (c.bisOpenFor === vid) return;
    c.bisOpenFor = vid;
    var v = c.data.variants[String(vid)] || {};
    emit('jl:bis-open', { variant_id: vid, size: v.size || '', source: source || 'pill' });
  }

  /* ---------------- render (idempotent) ---------------- */

  function render(c, reason) {
    if (!c) return null;
    var st = computeState(c);
    var S = c.data.strings;
    var root = c.root;

    /* Add to bag */
    var add = qs('[data-jl-add]', root);
    if (add) {
      var text;
      var disabled = false;
      var soldOut = false;
      if (st.needsSize) {
        if (st.soldOutAll) { text = S.soldOut; disabled = true; soldOut = true; }
        else { text = S.select; }
      } else if (!st.variantId) {
        text = S.unavailable || S.soldOut; disabled = true; soldOut = true;
      } else if (st.available) {
        text = fill(S.addPrice, { price: st.price });
      } else {
        text = S.soldOut; disabled = true; soldOut = true;
      }
      var label = qs('[data-jl-add-label]', add);
      if (!label) {
        add.textContent = '';
        label = document.createElement('span');
        label.className = 'jl-buy__label';
        label.setAttribute('data-jl-add-label', '');
        add.appendChild(label);
      }
      setText(label, text || '');
      if (!add.classList.contains('loading') && add.disabled !== disabled) add.disabled = disabled;
      add.classList.toggle('jl-buy__add--sold-out', soldOut);
    }

    /* hidden id input follows the radios (empty while a size is missing) */
    var form = qs('form[data-type="add-to-cart-form"]', root);
    var idInput = form ? qs('input[name="id"]', form) : null;
    if (idInput) {
      var want = st.variantId ? String(st.variantId) : '';
      if (idInput.value !== want && (want || st.needsSize)) idInput.value = want;
    }

    /* "Size: S" head (aria-hidden; the legend names the group) */
    var sizeLabel = qs('[data-jl-size-label]', root);
    if (sizeLabel && c.data.sizeWord) {
      var sizeFs = sizeFieldset(c);
      var checked = sizeFs ? qs('input[type="radio"]:checked', sizeFs) : null;
      setText(sizeLabel, checked ? fill(S.sizeValue, { size: checked.value }) : (S.sizeLegend || ''));
    }

    /* size error clears once every option is chosen (a failed add's message stays until the variant changes) */
    if (!st.needsSize && !(c.addErrFor && c.addErrFor === st.variantId)) {
      c.addErrFor = null;
      var err = qs('[data-jl-size-error]', root);
      if (err && err.textContent) err.textContent = '';
      var sizeBox = qs('[data-jl-size]', root);
      if (sizeBox) sizeBox.classList.remove('jl-size--error');
    }

    /* low stock */
    var low = qs('[data-jl-low-stock]', root);
    if (low) {
      setText(low, st.low || '');
      setHidden(low, !st.low);
    }

    /* hint: all sold out, or the notify link's "pick a struck size" until a size is chosen */
    var hint = qs('[data-jl-notify-hint]', root);
    if (hint) {
      if (st.soldOutAll) {
        setText(hint, S.allSoldOut || hint.textContent);
        setHidden(hint, false);
      } else if (!st.needsSize) {
        setHidden(hint, true);
      }
    }

    /* notify panel: open (without moving focus) for a chosen sold-out variant */
    if (st.variantId && !st.available) showNotify(c, st.variantId);
    else hideNotify(c);

    renderDelivery(c, st);

    var key = [st.variantId, st.needsSize, st.available].join('|');
    if (reason === 'init' || key !== c.lastKey) {
      c.lastKey = key;
      emit('jl:pdp-state', { state: st, reason: reason === 'init' ? 'init' : 'change' });
    }
    return st;
  }

  function announce(c, text) {
    var status = qs('[data-jl-pdp-status]', c.root);
    if (!status || !text) return;
    if (status.textContent === text) {
      status.textContent = '';
      window.setTimeout(function () { status.textContent = text; }, 60);
    } else {
      status.textContent = text;
    }
  }

  /* ---------------- public API ---------------- */

  function getState(sectionId) {
    var c = ctx(rootFor(sectionId));
    return c ? computeState(c) : null;
  }

  function selectSize(value, opts) {
    opts = opts || {};
    var c = ctx(rootFor(opts.section));
    if (!c) return false;
    var fs = sizeFieldset(c);
    if (!fs) return false;
    var radio = null;
    qsa('input[type="radio"]', fs).some(function (r) {
      if (r.value === String(value)) { radio = r; return true; }
      return false;
    });
    if (!radio) return false;
    if (!radio.checked) {
      c.pendingSource = opts.source || null;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      c.pendingSource = null;
    }
    return true;
  }

  function requireSize(source, sectionId) {
    var c = ctx(rootFor(sectionId));
    var src = source || 'inline';
    if (c) {
      var S = c.data.strings;
      /* AV7: the off-screen inline alert only for the inline form; the sticky bar shows (and announces) its own */
      var err = src === 'inline' ? qs('[data-jl-size-error]', c.root) : null;
      if (err) {
        c.addErrFor = null;
        if (err.textContent === S.required) {
          err.textContent = '';
          window.setTimeout(function () { err.textContent = S.required || ''; }, 60);
        } else {
          err.textContent = S.required || '';
        }
      }
      var sizeBox = qs('[data-jl-size]', c.root);
      if (sizeBox) sizeBox.classList.add('jl-size--error');
      if (src === 'inline') {
        /* first radio of the first fieldset that still has no choice (normally the size pills) */
        var target = null;
        var fsList = qsa('[data-jl-pdp-variants] fieldset', c.root);
        for (var i = 0; i < fsList.length && !target; i++) {
          if (!qs('input[type="radio"]:checked', fsList[i])) target = qs('input[type="radio"]', fsList[i]);
        }
        if (target) {
          try { target.focus({ preventScroll: false }); } catch (e) { target.focus(); }
        }
      }
    }
    emit('jl:pdp-size-required', { source: src });
    return false;
  }

  function openNotify(variantId, opts) {
    opts = opts || {};
    var c = ctx(rootFor(opts.section));
    if (!c || !variantId || !c.data.variants[String(variantId)]) return false;
    showNotify(c, variantId);
    bisOpen(c, variantId, opts.source || 'pill');
    if (opts.focus) {
      var panel = qs('[data-jl-notify]', c.root);
      var target = null;
      if (c.mode === 'inline') target = qs('[data-jl-notify-email]', panel);
      else if (c.mode === 'popup') target = qs('[data-jl-notify-popup]', panel);
      else target = qs('[id*="amp_back_in_stock"] button, [id*="amp_back_in_stock"] a[href]', c.root);
      if (target && !target.closest('[hidden]')) {
        try { target.focus({ preventScroll: false }); } catch (e) { target.focus(); }
      }
    }
    return true;
  }

  /* ---------------- events ---------------- */

  /* radio change: render, announce, open notify (one announcement per user change, L6) */
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.type !== 'radio' || !input.closest) return;
    if (!input.closest('[data-jl-pdp-variants]')) return;
    var c = ctx(input.closest('[data-jl-pdp]'));
    if (!c) return;
    var source = c.pendingSource || 'pill';
    var st = render(c, 'change');
    var S = c.data.strings;
    var isSize = !!input.closest('[data-jl-size-fieldset]');
    var addLabel = qs('[data-jl-add-label]', c.root);
    var msg;
    if (st.variantId && !st.available) {
      msg = fill(S.soldOutStatus, { size: st.size || input.value });
      bisOpen(c, st.variantId, source);
    } else if (st.variantId && isSize && c.data.sizeWord) {
      msg = fill(S.selected, { size: st.size || input.value });
      if (st.low) msg += ' ' + st.low;
    } else {
      msg = addLabel ? addLabel.textContent : '';
      if (st.low) msg += ' ' + st.low;
    }
    announce(c, msg);
  });

  /* L8: capture-phase guard, only the add form inside [data-jl-pdp] */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;
    var c = ctx(form.closest('[data-jl-pdp]'));
    if (!c) return;
    var st = computeState(c);
    if (st.needsSize) {
      e.preventDefault();
      e.stopImmediatePropagation();
      requireSize('inline', c.id);
      return;
    }
    if (!st.variantId || !st.available) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    var idInput = qs('input[name="id"]', form);
    if (idInput && idInput.value !== String(st.variantId)) idInput.value = String(st.variantId);
    /* AD3: product-form.js posts /cart/add in this same task; the fetch watch below reports a failure */
    var source = form.__jlAddSource === 'sticky' ? 'sticky' : 'inline';
    form.__jlAddSource = null;
    clearAddError(c);
    addPending = { c: c, source: source, variantId: st.variantId };
    window.setTimeout(function () { addPending = null; }, 0);
  }, true);

  /* ---------------- failed adds (AD3) ---------------- */

  function clearAddError(c) {
    if (!c.addErrFor) return;
    c.addErrFor = null;
    var err = qs('[data-jl-size-error]', c.root);
    if (err) err.textContent = '';
    emit('jl:pdp-add-error', { section: c.id, message: '', source: null });
  }

  /* one visible message and one announcement: the inline alert for an inline add; for a sticky add only the
     sticky bar's alert (assets/jl-sticky-atc.js listens to jl:pdp-add-error), as for the missing size (AV7) */
  function showAddError(pend, message) {
    var c = pend.c;
    if (!c || !document.documentElement.contains(c.root)) return;
    var S = c.data.strings;
    var text = String(message || '').replace(/<[^>]*>/g, '').replace(/\s*\u2014\s*/g, ', ').trim() || S.addError || '';
    if (!text) return;
    c.addErrFor = pend.variantId;
    if (pend.source !== 'sticky') {
      var err = qs('[data-jl-size-error]', c.root);
      if (err) err.textContent = text;
    }
    emit('jl:pdp-add-error', { section: c.id, message: text, source: pend.source, variant_id: pend.variantId });
  }

  /* a fetch wrapper that only looks at /cart/add responses of a PDP add: it reads a clone (the original response
     goes to product-form.js untouched) and passes every other request straight through; installed once */
  function watchAdds() {
    if (window.__jlPdpFetchWatch || typeof window.fetch !== 'function') return;
    window.__jlPdpFetchWatch = true;
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var result = origFetch.apply(this, arguments);
      var pend = addPending;
      var url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
      if (!pend || url.indexOf('/cart/add') === -1) return result;
      addPending = null;
      result.then(function (res) {
        if (res && res.ok) return;
        var copy = null;
        try { copy = res.clone(); } catch (e) { copy = null; }
        if (!copy) { showAddError(pend, ''); return; }
        copy.json().then(function (json) {
          showAddError(pend, json && (json.description || json.message));
        }, function () {
          showAddError(pend, '');
        });
      }, function () {
        showAddError(pend, '');
      });
      return result;
    };
  }

  /* notify form (bubbling; never the add form) */
  document.addEventListener('submit', function (e) {
    var form = e.target && e.target.closest ? e.target.closest('[data-jl-notify-form]') : null;
    if (!form) return;
    e.preventDefault();
    var c = ctx(form.closest('[data-jl-pdp]'));
    if (!c || c.pending) return;
    var S = c.data.strings;
    var email = qs('[data-jl-notify-email]', form);
    var submit = qs('[data-jl-notify-submit]', form);
    var value = email ? String(email.value || '').trim() : '';
    if (!EMAIL_RE.test(value)) {
      if (email) {
        email.setAttribute('aria-invalid', 'true');
        email.focus();
      }
      setNotifyMsg(c, S.notifyErrorEmail, 'error');
      return;
    }
    if (email) email.removeAttribute('aria-invalid');
    var vid = c.notifyFor;
    if (!vid || bisMode() !== 'inline') {
      applyMode(c);
      return;
    }
    var size = (c.data.variants[String(vid)] || {}).size || '';
    var finished = false;
    c.pending = true;
    if (submit) submit.disabled = true;
    setNotifyMsg(c, '', null);
    var finish = function (ok) {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      c.pending = false;
      if (submit) submit.disabled = false;
      if (ok) {
        c.notifyDone[vid] = true;
        emit('jl:bis-signup', { variant_id: vid, size: size, method: 'inline' });
      }
      /* AD4: the reply always counts for its own size; the panel only changes while that size is still shown */
      var panel = qs('[data-jl-notify]', c.root);
      if (c.notifyFor !== vid || !panel || panel.hidden) return;
      if (ok) {
        var msg = qs('[data-jl-notify-msg]', c.root);
        var active = document.activeElement;
        var inForm = !active || active === document.body || active === document.documentElement || form.contains(active);
        setNotifyMsg(c, fill(S.notifySuccess, { size: size }), 'ok');
        /* AV8/AD5: focus would fall to <body> with the hidden form; it moves to the confirmation */
        if (msg && inForm) {
          msg.setAttribute('tabindex', '-1');
          try { msg.focus({ preventScroll: true }); } catch (e) { msg.focus(); }
        }
        setHidden(form, true);
        setHidden(qs('.jl-notify__privacy', c.root), true);
      } else {
        setNotifyMsg(c, S.notifyError, 'error');
      }
    };
    var timer = window.setTimeout(function () { finish(false); }, BIS_TIMEOUT);
    var promise;
    try {
      promise = Promise.resolve(window.BIS.create(value, vid, Number(c.data.product), { accepts_marketing: false }));
    } catch (err) {
      promise = Promise.reject(err);
    }
    promise.then(function (res) {
      finish(!!(res && res.status === 'OK'));
    }, function () {
      finish(false);
    });
  });

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    /* Amp popup fallback */
    var popup = t.closest('[data-jl-notify-popup]');
    if (popup) {
      var pc = ctx(popup.closest('[data-jl-pdp]'));
      if (pc && pc.notifyFor && window.BIS && window.BIS.popup && typeof window.BIS.popup.show === 'function') {
        window.BIS.popup.show({ variantId: Number(pc.notifyFor) });
        var pv = pc.data.variants[String(pc.notifyFor)] || {};
        emit('jl:bis-open', { variant_id: Number(pc.notifyFor), size: pv.size || '', source: 'popup' });
      }
      return;
    }

    /* L39 notify link: sold-out size chosen -> focus the email field; otherwise point at the struck sizes */
    var link = t.closest('[data-jl-notify-link]');
    if (link) {
      var c = ctx(link.closest('[data-jl-pdp]'));
      if (!c) return;
      var st = computeState(c);
      if (st.variantId && !st.available) {
        openNotify(st.variantId, { focus: true, source: 'link', section: c.id });
        return;
      }
      var hint = qs('[data-jl-notify-hint]', c.root);
      if (hint) {
        setText(hint, c.data.strings.notifyPick || '');
        setHidden(hint, false);
        /* AV9: the hint is not a live region; say it through the status region */
        announce(c, c.data.strings.notifyPick || '');
      }
      var fs = sizeFieldset(c);
      var first = fs ? qs('input[type="radio"][data-jl-available="false"]', fs) : null;
      if (first) {
        try { first.focus({ preventScroll: false }); } catch (err) { first.focus(); }
      }
    }
  });

  /* Hearts (phase 4, K16): the theme wishlist in assets/jl-lists.js marks and toggles every [data-jl-wishlist]
     button; the phase 3 Swym mirror, click queue and re-init are gone. */

  /* ---------------- live review counts (reviews.io rating-batch) ----------------
     The reviews.rating / rating_count metafields are stale or missing for many products (15 Oct), so the rating row
     (snippets/jl-rating-row.liquid, data-jl-rating-api) and the Polaris box in sections/jl-reviews.liquid
     (data-jl-polaris-min) are corrected from reviews.io's public rating-batch API, the call its own rating snippet makes.
     One request per page. The API answers one entry per reviewed SKU; the product's count is their sum and its score
     the count-weighted mean. Below data-min: a row the server printed hidden stays hidden. No answer: nothing changes. */
  var ratingReq = null;

  function ratingData(skus) {
    if (ratingReq) return ratingReq;
    var url = 'https://api.reviews.io/product/rating-batch?store=janelushka.com&sku=' + encodeURIComponent(skus);
    ratingReq = window.fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (rows) {
        if (!Array.isArray(rows)) return null;
        var seen = {}, n = 0, sum = 0;
        rows.forEach(function (row) {
          if (!row || seen[row.sku]) return;
          seen[row.sku] = true;
          var c = parseInt(row.num_ratings, 10) || 0;
          var avg = parseFloat(row.average_rating) || 0;
          n += c;
          sum += c * avg;
        });
        return { count: n, score: n ? sum / n : 0 };
      })
      .catch(function () { return null; });
    return ratingReq;
  }

  function groupDigits(n, sep) {
    var s = String(n), out = '';
    for (var i = 0; i < s.length; i++) {
      if (i > 0 && (s.length - i) % 3 === 0) out += sep;
      out += s.charAt(i);
    }
    return out;
  }

  function applyRating(row, d) {
    var min = parseInt(row.getAttribute('data-min'), 10) || 1;
    if (d.count < min) return;
    var r10 = Math.round(d.score * 10);
    var dec = row.getAttribute('data-dec') || '.';
    var scoreTxt = Math.floor(r10 / 10) + dec + (r10 % 10);
    var tpl = d.count === 1 ? row.getAttribute('data-t-one') : row.getAttribute('data-t');
    if (!tpl) return;
    var text = tpl.replace('[s]', scoreTxt).replace('[c]', groupDigits(d.count, row.getAttribute('data-thou') || ','));
    var t = qs('.jl-rating__text', row);
    if (t) t.textContent = text;
    var stars = qs('.jl-rating__stars', row);
    if (stars) stars.style.setProperty('--jl-rating', (r10 / 10).toFixed(1));
    var link = qs('[data-jl-rating-link]', row);
    if (link) link.setAttribute('data-mode', 'product');
    row.classList.remove('is-pending');
    row.hidden = false;
  }

  function liveRatings(root) {
    var row = qs('[data-jl-rating-api]', root);
    if (!row || row.__jlApi || !window.fetch) return;
    row.__jlApi = true;
    ratingData(row.getAttribute('data-jl-rating-api')).then(function (d) {
      if (!d) return;
      applyRating(row, d);
      qsa('[data-jl-polaris-min]').forEach(function (box) {
        var min = parseInt(box.getAttribute('data-jl-polaris-min'), 10) || 0;
        if (d.count >= min && d.count > 0) box.hidden = false;
      });
    });
  }

  /* ---------------- init ---------------- */

  function watchAdd(c) {
    var add = qs('[data-jl-add]', c.root);
    if (!add || add.__jlWatched || !window.MutationObserver) return;
    add.__jlWatched = true;
    var wasLoading = add.classList.contains('loading');
    new MutationObserver(function () {
      var loading = add.classList.contains('loading');
      /* product-form.js re-enables the button after an add: put the state back */
      if (wasLoading && !loading) render(ctx(c.root), 'change');
      wasLoading = loading;
    }).observe(add, { attributes: true, attributeFilter: ['class'] });
  }

  function initRoot(root) {
    if (!root || root.__jlPdpInit) return;
    root.__jlPdpInit = true;
    var c = ctx(root);

    /* rating row: no dead link when the reviews section is missing */
    qsa('[data-jl-rating-link]', root).forEach(function (a) {
      if (!document.getElementById('jl-reviews')) a.removeAttribute('href');
    });

    liveRatings(root);
    watchAdd(c);
    var st = render(c, 'init');
    emit('jl:pdp-ready', { state: st });
    warmClock();
  }

  /* PF1: build the Amsterdam formatter while the page is idle, not inside the first size tap */
  var clockWarm = false;
  function warmClock() {
    if (clockWarm) return;
    clockWarm = true;
    var run = function () { try { amsFormatter(); } catch (e) { /* dispatchState falls back to 'next' */ } };
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 3000 });
    else window.setTimeout(run, 1);
  }

  function init() {
    qsa('[data-jl-pdp]').forEach(initRoot);
  }

  function patchVariantRadios() {
    var Ctor = window.customElements && window.customElements.get('variant-radios');
    if (!Ctor) return;
    var proto = Ctor.prototype;
    if (Object.prototype.hasOwnProperty.call(proto, '__jlPatched')) return;
    var origToggle = proto.toggleAddButton;
    var origUnavailable = proto.setUnavailable;
    var origRenderInfo = proto.renderProductInfo;
    /* AD7: variants.js copies #price from every ?section_id= reply, also from a slow one for a size that is no
       longer chosen. Same request and copy for the PDP, but a reply for a variant that is not current is dropped. */
    proto.renderProductInfo = function () {
      var self = this;
      var variant = this.currentVariant;
      if (!(this.hasAttribute && this.hasAttribute('data-jl-pdp-variants')) || !variant || typeof window.fetch !== 'function') {
        return typeof origRenderInfo === 'function' ? origRenderInfo.apply(this, arguments) : undefined;
      }
      var section = this.dataset.section;
      window.fetch(this.dataset.url + '?variant=' + variant.id + '&section_id=' + section)
        .then(function (response) { return response.text(); })
        .then(function (text) {
          if (!self.currentVariant || self.currentVariant.id !== variant.id) return;
          var source = new DOMParser().parseFromString(text, 'text/html').getElementById('price-' + section);
          qsa('[id="price-' + section + '"]').forEach(function (dest) {
            if (source) dest.innerHTML = source.innerHTML;
            dest.classList.remove('visibility-hidden');
          });
          self.toggleAddButton(!self.currentVariant.available, window.variantStrings && window.variantStrings.soldOut);
        })
        .catch(function () { /* the data map already shows the price and the ADD label */ });
    };
    proto.toggleAddButton = function () {
      if (this.hasAttribute && this.hasAttribute('data-jl-pdp-variants')) {
        var c = ctx(this.closest('[data-jl-pdp]'));
        if (c) { render(c, 'change'); return; }
      }
      return typeof origToggle === 'function' ? origToggle.apply(this, arguments) : undefined;
    };
    proto.setUnavailable = function () {
      if (this.hasAttribute && this.hasAttribute('data-jl-pdp-variants')) {
        var c = ctx(this.closest('[data-jl-pdp]'));
        if (c) { render(c, 'change'); return; }
      }
      return typeof origUnavailable === 'function' ? origUnavailable.apply(this, arguments) : undefined;
    };
    Object.defineProperty(proto, '__jlPatched', { value: true });
  }

  window.JLPdp = {
    __v12: true,
    getState: getState,
    selectSize: selectSize,
    requireSize: requireSize,
    openNotify: openNotify,
    render: function (sectionId) { return render(ctx(rootFor(sectionId)), 'change'); },
    _dispatchState: dispatchState
  };

  if (window.customElements && typeof window.customElements.whenDefined === 'function') {
    window.customElements.whenDefined('variant-radios').then(patchVariantRadios);
  }

  watchAdds();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else window.setTimeout(init, 0);

  /* Amp loads late: check the notify mode again once everything is in */
  window.addEventListener('load', function () {
    qsa('[data-jl-pdp]').forEach(function (root) {
      var c = ctx(root);
      if (c && c.notifyFor) applyMode(c);
    });
  });

  /* back/forward cache: radios may come back checked */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    qsa('[data-jl-pdp]').forEach(function (root) { render(ctx(root), 'change'); });
  });

  /* theme editor: the section is replaced */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-jl-pdp]') : null;
    if (!root) return;
    initRoot(root);
  });
})();
